import { useState, useEffect, useRef } from "react";
import { FormattedMessage } from "react-intl";
import cn from "classnames";

// @mui
import { Box, Button, IconButton, Skeleton } from "@mui/material";

// Components
import CustomIcon from "components/CustomIcon";
import PaceLabsPopover from "components/PaceLabsDashboard/PaceLabsPopover";
import { SimpleBreadcrumb } from "components/PaceLabsDashboard/Breadcrumbs";

// Hooks
import { useAppDispatch, useAppSelector } from "app/hooks";
import usePaceLabsInsights from "./usePaceLabsInsights";

// Store
import { getPaceLabsLspm } from "store/slice/PaceLabs/paceLabsLSPM";
import { fetchPaceLabsCharts, getPaceLabsChartsAll } from "store/slice/PaceLabs/paceLabsCharts";

// Types
import { ChartNames } from "store/slice/charts";
import { BreadcrumbStep } from "components/PaceLabsDashboard/Breadcrumbs";

// Const
import icons from "enums/icons";
import { getPaceLabsChart } from "./chartsConfig";
import { PACE_LABS_CHARTS } from "constants/constants";
import { ChartIds, ChartRouteNamesPaceLabs } from "enums/chartEnums";
import { SwitchTransition, CSSTransition } from "react-transition-group";

// utils
import useThrottledResizeObserver from "utils/hooks/useThrottledResizeObserver";

// Styles
import "./animations.css";
import { paceLabsInsightsStyles } from "./PaceLabsInsights.styles";
import { getUseNewFunctionalData } from "store/slice/UI";

const InsightsChart = ({ chartName }: { chartName: string | undefined }) => {
    const classes = paceLabsInsightsStyles();

    const dispatch = useAppDispatch();
    const { ref, width, height } = useThrottledResizeObserver();
    const { data: chartData, status } = useAppSelector(getPaceLabsChartsAll);
    const { paceDataStatus: LSPMRequestPaceInfo } = useAppSelector(getPaceLabsLspm);

    const graphingByFunction = useAppSelector(getUseNewFunctionalData);
    const graphingByFunctionPrevValue = useRef(graphingByFunction);

    const [chartId, setChartId] = useState<ChartNames | undefined>();

    useEffect(() => {
        // Simultaneous requests to lspm and charts may cause a 500 error, to avoid it
        // we are making sure lspm has already being done (either done or returned an error)
        if (chartData === null && status !== "fetching" && status !== "error" && LSPMRequestPaceInfo === "done") {
            dispatch(fetchPaceLabsCharts({ charts: PACE_LABS_CHARTS, graphingByFunction }));
        }
    }, [chartData, status, LSPMRequestPaceInfo, dispatch, graphingByFunction]);

    useEffect(() => {
        if (graphingByFunctionPrevValue.current !== graphingByFunction) {
            dispatch(fetchPaceLabsCharts({ charts: PACE_LABS_CHARTS, graphingByFunction }));
            graphingByFunctionPrevValue.current = graphingByFunction;
        }
    }, [dispatch, graphingByFunction]);

    useEffect(() => {
        if (!chartName) return;

        const routeKey = Object.entries(ChartRouteNamesPaceLabs).find((key) => key[1] === chartName);

        if (routeKey) {
            const chartId = Object.entries(ChartIds).find((key) => key[0] === routeKey[0]);
            chartId && setChartId(chartId[1]);
        }
    }, [chartName]);

    return status === "pristine" || status === "fetching" ? (
        <Skeleton animation="wave" variant="rectangular" className={classes.chartWrapperInner} />
    ) : (
        <Box ref={ref} className={classes.chartWrapperInner}>
            {chartData && chartId ? getPaceLabsChart(chartId)?.graphComponent(width, height) : null}
        </Box>
    );
};

const PaceLabsInsights = ({
    chartName,
    stepSelected,
    chartSelected,
    rootStepPosition,
    transitionTo,
    hasCompletedTheAssessment,
}: {
    chartName: string | undefined;
    stepSelected: BreadcrumbStep | null;
    chartSelected: BreadcrumbStep | null;
    rootStepPosition: number;
    transitionTo: React.MutableRefObject<"slide-left" | "slide-right" | "slide-up" | "slide-down">;
    hasCompletedTheAssessment: boolean;
}) => {
    const classes = paceLabsInsightsStyles();

    const { showParentLink, onClickNavigateRootStep, onClickNavigateChildrenStep, onClickNavigateParent } =
        usePaceLabsInsights({ stepSelected, chartSelected, rootStepPosition, transitionTo });

    return (
        <>
            <Box className={classes.insightBody}>
                {stepSelected && chartSelected ? (
                    <>
                        <Box
                            className={classes.insightNavMenu}
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            mb={1}
                        >
                            <Box display="flex" alignItems="center">
                                <IconButton onClick={() => onClickNavigateRootStep(-1)} color="primary">
                                    <CustomIcon icon={icons.circledChevronLeft} />
                                </IconButton>
                                <SimpleBreadcrumb step={stepSelected} variant="large" />
                                <IconButton onClick={() => onClickNavigateRootStep(1)} color="primary">
                                    <CustomIcon icon={icons.circledChevronRight} />
                                </IconButton>
                            </Box>

                            {chartSelected.route !== "profile" && chartSelected.popover && (
                                <PaceLabsPopover
                                    title={chartSelected.title}
                                    content={<FormattedMessage id={chartSelected.popover} />}
                                />
                            )}
                        </Box>

                        {chartSelected.route !== "profile" && chartSelected.description && (
                            <Box className={classes.insightDescription}>
                                <FormattedMessage id={chartSelected.description} />
                            </Box>
                        )}

                        <SwitchTransition mode="out-in">
                            <CSSTransition
                                key={chartName}
                                classNames={transitionTo.current}
                                addEndListener={(node, done) => {
                                    node.addEventListener("transitionend", done, false);
                                }}
                            >
                                <Box className={cn(chartName, classes.insightWrapper)}>
                                    <Box className={classes.chartWrapper}>
                                        {showParentLink ? (
                                            <Box>
                                                <Button
                                                    className={classes.goToChartButton}
                                                    onClick={() => onClickNavigateParent(stepSelected.route)}
                                                >
                                                    {`Go back to ${stepSelected.title}`}
                                                    <CustomIcon icon={icons.chevronUpThin} />
                                                </Button>

                                                {chartName === ChartRouteNamesPaceLabs.Situational && (
                                                    <Button
                                                        className={classes.goToChartButton}
                                                        onClick={() =>
                                                            onClickNavigateChildrenStep(ChartRouteNamesPaceLabs.Domain)
                                                        }
                                                    >
                                                        VIew your Domain Breakdown
                                                        <CustomIcon icon={icons.chevronDownThin} />
                                                    </Button>
                                                )}

                                                {chartName === ChartRouteNamesPaceLabs.Domain && (
                                                    <Button
                                                        className={classes.goToChartButton}
                                                        onClick={() =>
                                                            onClickNavigateChildrenStep(
                                                                ChartRouteNamesPaceLabs.Situational
                                                            )
                                                        }
                                                    >
                                                        VIew your Situational Breakdown
                                                        <CustomIcon icon={icons.chevronUpThin} />
                                                    </Button>
                                                )}
                                            </Box>
                                        ) : null}

                                        <InsightsChart chartName={chartName} />

                                        {chartSelected && chartSelected.children && chartSelected.children.length ? (
                                            <Box display="flex">
                                                {chartSelected.children.map((childStep, index) => (
                                                    <Button
                                                        className={classes.goToChartButton}
                                                        key={index}
                                                        onClick={() => onClickNavigateChildrenStep(childStep.route)}
                                                    >
                                                        {childStep.title}
                                                        <CustomIcon icon={icons.chevronDownThin} />
                                                    </Button>
                                                ))}
                                            </Box>
                                        ) : null}
                                    </Box>
                                </Box>
                            </CSSTransition>
                        </SwitchTransition>
                    </>
                ) : null}
            </Box>
        </>
    );
};

export default PaceLabsInsights;
